import { faComments } from '@fortawesome/free-solid-svg-icons';
import { CustomerEmptyState } from '@/components/customer-portal/CustomerEmptyState';
import type {
  CustomerThreadEvent,
  CustomerThreadEventItem,
} from '@/types/customer-request-thread.types';

/**
 * Stage D-iv — the customer's conversation timeline (D-145 / OD-D4).
 *
 * ── THIS COMPONENT DECIDES NOTHING ABOUT VISIBILITY ─────────────────────────
 * It renders `thread.timeline` exactly as the server sent it. There is no
 * `.filter()` here and there must never be one: the D-ii query already applied
 * both the `visibility = CUSTOMER` predicate and the entry-type allowlist IN THE
 * DATABASE, so an entry a customer may not see was never loaded, let alone sent.
 * A frontend filter would be a second visibility policy — one that could drift
 * from the real one, and one that would imply the response is unsafe to render.
 * It is safe to render, which is the whole point of doing the work server-side.
 *
 * ── WHY EVERY QUESTION LOOKS THE SAME ───────────────────────────────────────
 * `QUESTION_ASKED` arrives for a question Commercial asked (T4/T12) AND for one
 * Operations asked through Commercial (the T6 verdict). The server collapsed
 * them deliberately, and this component must not un-collapse them: one label,
 * one icon, one tone, one accessible name, no tooltip and no variant copy. A
 * customer who could tell the two apart would learn that Operations was
 * consulted and that their request was routed internally — exactly the
 * knowledge D-131 OQ-4 forbids exposing. There is no branch below that could
 * reintroduce the distinction, because the origin never reaches this file.
 *
 * ── ORDER COMES FROM THE SERVER ─────────────────────────────────────────────
 * Rendered in array order. The server sorts ascending by `createdAt` in the
 * query; nothing here sorts, reverses, groups or infers chronology from the
 * event type.
 */

/** The ONLY labels a customer may see. Keyed by the customer vocabulary — the
 * internal entry type never reaches the browser, so it cannot be rendered even
 * by mistake. */
const EVENT_LABEL: Record<CustomerThreadEvent, string> = {
  YOUR_REQUEST: 'Your request',
  YOU_ADDED: 'You added information',
  QUESTION_ASKED: 'Question from the service team',
  YOUR_REPLY: 'Your reply',
  QUOTATION_SENT: 'Quotation sent',
  YOUR_DECISION: 'Your decision',
  UPDATE_FROM_TEAM: 'Update from the service team',
  REQUEST_CLOSED: 'Request closed',
};

/**
 * A label for an event this build does not recognise.
 *
 * Deliberately NOT a drop: discarding an unknown event would make this component
 * a filter, and would hide something the server chose to send. Deliberately not
 * the raw value either — an unrecognised token is printed nowhere. The customer
 * sees a truthful, neutral entry instead.
 */
const UNKNOWN_EVENT_LABEL = 'Update';

/** The two attributions that exist. No name, no email, no id, no department, no
 * role — the DTO carries none of them, so there is nothing here to reveal, and
 * a backfilled event with no recorded actor renders exactly like any other. */
const AUTHOR_LABEL: Record<CustomerThreadEventItem['author'], string> = {
  YOU: 'You',
  SERVICE_TEAM: 'Service team',
};

function fmtDateTime(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function CustomerThreadTimeline({ events }: { events: CustomerThreadEventItem[] }) {
  if (events.length === 0) {
    // An empty conversation is a normal state, not an error and not something to
    // fill in: a request opened a minute ago has nothing on it yet.
    return (
      <CustomerEmptyState
        icon={faComments}
        title="Nothing here yet"
        description="Messages between you and the service team will appear here."
      />
    );
  }

  return (
    <ol className="space-y-3" data-testid="customer-thread-timeline">
      {events.map((event) => {
        const label = EVENT_LABEL[event.event] ?? UNKNOWN_EVENT_LABEL;
        const author = AUTHOR_LABEL[event.author] ?? AUTHOR_LABEL.SERVICE_TEAM;
        const body = event.body?.trim() ? event.body : null;

        return (
          <li
            key={event.id}
            data-testid="customer-thread-event"
            className="rounded-customer-sm border border-customer-border bg-customer-surface p-3.5"
          >
            {/* Label, attribution and time are all TEXT, so the entry is
                understandable without colour and reads correctly to a screen
                reader in the order it is written. `flex-wrap` keeps a long
                label and a long timestamp on separate lines on a phone rather
                than widening the page. */}
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-sm font-semibold text-customer-text">{label}</span>
              <span className="text-xs text-customer-text-secondary">· {author} ·</span>
              <time dateTime={event.createdAt} className="text-xs text-customer-text-secondary">
                {fmtDateTime(event.createdAt)}
              </time>
            </div>

            {/* A bodyless event — `QUOTATION_SENT` is written with no body by
                design — is a milestone, not a broken message. It keeps its
                label, attribution and time, and simply has no paragraph; it is
                never dropped, and never rendered as an empty box or the words
                "null"/"undefined". */}
            {body !== null && (
              <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 text-customer-text">
                {body}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
}
